/* eslint-disable-next-line react/jsx-props-no-spreading */
import * as React from "react";
import * as _ from "underscore";

import { Button } from "carbon-components-react";
import { Save16, Copy16, Paste16, Add16 } from "@carbon/icons-react";

import { DocFieldShape, DocSectionShape, DocShape } from "@eave/shared/types/api";
import {
  DocumentFieldUpdate,
  createDocumentFieldsDiff,
  saveDocumentUpdates,
} from "underwriter_dashboard/shared/Documents/api-helpers";
import { documentOwnerOptions } from "underwriter_dashboard/shared/Documents/helpers";
import AddressFieldsWithValidation from "web_modules/AddressFieldsWithValidation";
import LabeledInput from "web_modules/LabeledInput";

import DocDisplay from "./DocDisplay";
import { doesFieldTypeMatchValueFormat } from "./DocEntry";
import { loadApp } from "../actions";
import DocChangerModule, { getInitialState } from "./DocChangerModule";

require("../../../assets/stylesheets/components/underwriter_dashboard/shared/doc_entry.scss");
const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");
const classnames = require("classnames");
const humanize = require("underscore.string/humanize");
const numeral = require("numeral");

const updateClipboard = newClip => {
  navigator.clipboard.writeText(newClip).then(
    () => {
      /* clipboard successfully set */
    },
    () => {
      /* clipboard write failed */
    },
  );
};

interface Props {
  app?: any;
  docs: {
    length: number;
    map: Function;
    [index: number]: DocShape;
  };
  docNotFoundMsg: string;
  modalTitle: string;
  hideButton: boolean;
  hurdle: any;
  values: any;
  upsertValue: (
    sectionKey: string,
    sectionIndex: number,
    fieldKey: string,
    fieldValue: any,
  ) => void;
  deleteValue: (sectionKey: string, sectionIndex: number, fieldKey: string) => void;
  onHide: Function;
  onSave?: Function; // Hurdles can OPTIONALLY specify additional save() functions to be called along with the standard document-field saving
  dispatch?: Function;
  addAnotherSection: (sectionKey: string, indexOfSectionToAddAfter: number) => void;
}

class DocEntryView extends React.Component<Props, any> {
  static initialState(docForSidebar) {
    return {
      basicDocFields: getInitialState(docForSidebar),
    };
  }

  static generateFieldLabel(field) {
    const {
      field_key,
      field_value,
      field_type,
      display_text,
      effect_on_income,
      borrower_value,
      field_value_options,
    } = field;

    let fieldLabel = display_text || humanize(field_key);

    fieldLabel += DocEntryView.getEffectText(effect_on_income);

    const typeMatches = doesFieldTypeMatchValueFormat(field_type, field_value, field_value_options);
    if (!typeMatches) {
      // TODO add styling instead
      fieldLabel += ` (MALFORMED VALUE, PLEASE FIX. STORED VALUE: ${field_value})`;
    }

    if (borrower_value) {
      fieldLabel += ` <<< Borrower correction: $${numeral(borrower_value).format("0,0")}`;
    }

    return fieldLabel;
  }

  constructor(props) {
    super(props);
    const { docs } = props;
    this.state = docs && docs.length > 0 ? DocEntryView.initialState(docs[0]) : {};
  }

  onChangeBoolean = (sectionKey: string, sectionIndex: number, e) => {
    const { name, checked } = e.target;
    const { upsertValue, deleteValue } = this.props;

    if (checked) {
      upsertValue(sectionKey, sectionIndex, name, true);
    } else {
      deleteValue(sectionKey, sectionIndex, name);
    }
  };

  onChangeText = (sectionKey: string, sectionIndex: number, e) => {
    const { name, value } = e.target;
    const { upsertValue, deleteValue } = this.props;

    if (value.length) {
      upsertValue(sectionKey, sectionIndex, name, value);
    } else {
      deleteValue(sectionKey, sectionIndex, name);
    }
  };

  onChangeAddress = (sectionKey: string, sectionIndex: number, fieldKey: string, values) => {
    const { upsertValue, deleteValue } = this.props;

    if (_.some(values)) {
      upsertValue(sectionKey, sectionIndex, fieldKey, values);
    } else {
      deleteValue(sectionKey, sectionIndex, fieldKey);
    }
  };

  onChangeMoney = (sectionKey: string, sectionIndex: number, e) => {
    const { name, value } = e.target;
    const { upsertValue, deleteValue } = this.props;

    const parsed = parseFloat(value); // value comes from MoneyInput as string
    if (value.length && !_.isNaN(parsed)) {
      upsertValue(sectionKey, sectionIndex, name, parsed);
    } else {
      deleteValue(sectionKey, sectionIndex, name);
    }
  };

  onChangeNumber = (sectionKey: string, sectionIndex: number, e) => {
    const { name, value } = e.target;
    const { upsertValue, deleteValue } = this.props;

    // allow only numeric values optionally with a decimal part
    // need a special case for the transition with a decimal point at the end
    if (/^\.$/.test(value)) {
      upsertValue(sectionKey, sectionIndex, name, "0.");
    } else if (/^\d+\.$/.test(value)) {
      upsertValue(sectionKey, sectionIndex, name, value);
    } else if (/^\d+(\.\d+)?$/.test(value)) {
      upsertValue(sectionKey, sectionIndex, name, value);
    } else if (!value.length) {
      deleteValue(sectionKey, sectionIndex, name);
    }
  };

  onChangeSelect = (sectionKey: string, sectionIndex: number, fieldKey: string, option) => {
    const { upsertValue, deleteValue } = this.props;

    if (option && option.value && option.value.length) {
      upsertValue(sectionKey, sectionIndex, fieldKey, option.value);
    } else {
      deleteValue(sectionKey, sectionIndex, fieldKey);
    }
  };

  setRef = el => {
    this.sidebar = el;
  };

  static getEffectText(effect) {
    if (effect === "add") {
      return " (+)";
    }
    if (effect === "subtract") {
      return " (-)";
    }
    if (effect === "negate") {
      return " (+/-)";
    }
    return "";
  }

  updateDocDataState = (basicDocFields: any) => this.setState({ basicDocFields });

  // The back-end update endpoint expects DIFF objects - this function prepares them and calls the update function
  prepareDocumentFieldUpdatePayload = () => {
    const { docs, values } = this.props;
    const doc = docs && docs[0];

    // Some "basic" fields about each document: borrower_entity, whether its used or not, etc
    const {
      borrower_entity,
      owner_entity_id,
      owner_entity_type,
      revised_filename,
      type,
      newUsed,
      newVisibility,
    } = this.state.basicDocFields;

    // Update certain fields on the document
    const updatedDocument = {
      ...doc,
      borrower_entity,
      owner_entity_id,
      owner_entity_type,
      revised_filename,
      type,
    };

    // Use a helper function to create an array of "diff objects" to send as the payload
    const documentFieldsUpdateDiff = createDocumentFieldsDiff(doc, values, newUsed, newVisibility);

    return { updatedDocument, documentFieldsUpdateDiff };
  };

  saveValues = () => {
    const { updatedDocument, documentFieldsUpdateDiff } = this.prepareDocumentFieldUpdatePayload();
    this.onSave(updatedDocument, documentFieldsUpdateDiff);
  };

  // Fires off the update requests to the back-end
  onSave = (doc, documentFieldsDiff: DocumentFieldUpdate[]) => {
    const { app, dispatch, onSave, values } = this.props;

    if (onSave) {
      saveDocumentUpdates(doc, documentFieldsDiff, onSave(values)).then(() => {
        loadApp(dispatch, app.id);
      });
    } else {
      saveDocumentUpdates(doc, documentFieldsDiff).then(() => {
        loadApp(dispatch, app.id);
      });
    }
  };

  private sidebar: HTMLDivElement;

  renderSidebar() {
    const { app, docs, hideButton, addAnotherSection } = this.props;

    const docForSidebar = docs[0];

    if (!docForSidebar) {
      return null;
    }

    // TODO: jjw: refactor this whole block into smaller helpers
    const calculationSection = docForSidebar.calculations &&
      docForSidebar.calculations.section_key && (
        <div key={docForSidebar.calculations.section_key} className="doc-entry__section">
          <div className="doc-entry__section-container">
            <div className="doc-entry__section-header">
              <strong className="doc-entry__section-key">
                {docForSidebar.calculations.display_text ||
                  humanize(docForSidebar.calculations.section_key)}
              </strong>
              <div
                className="doc-entry__section-desc"
                dangerouslySetInnerHTML={{ __html: docForSidebar.calculations.section_desc }}
              />
            </div>
            {docForSidebar.calculations.fields.map(field => {
              const fieldClasses = classnames({
                "doc-entry__field-input": true,
                "doc-entry__field-input--checkbox": field.field_type === "boolean",
              });

              return (
                <div key={`${docForSidebar.calculations.section_key}-${field.field_key}`}>
                  <div className="doc-entry__field">
                    <div className={fieldClasses}>
                      {this.renderField(docForSidebar.calculations, field, true)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );

    return (
      <div className="doc-entry__sidebar">
        <div ref={this.setRef} className="doc-entry__sidebar-container">
          <DocChangerModule
            app={app}
            showFormOnly
            hideButton={hideButton}
            updateParentState={this.updateDocDataState}
            key={docForSidebar.id}
            doc={docForSidebar}
            ownerOptions={documentOwnerOptions(app)}
            onSave={(doc, diff) => this.onSave(doc, diff)}
          />
          {calculationSection}
          {docForSidebar.sections.map((section: DocSectionShape, indexOfSection: number) => {
            if (["used", "visibility"].includes(section.section_key)) {
              return null;
            }
            return (
              <div
                key={`${section.section_key}-${section.section_index}`}
                className="doc-entry__section"
              >
                <div className="doc-entry__section-container">
                  <div className="doc-entry__section-header">
                    <strong className="doc-entry__section-key">
                      {section.display_text || humanize(section.section_key)}
                    </strong>
                    <div
                      className="doc-entry__section-desc"
                      dangerouslySetInnerHTML={{ __html: section.section_desc }}
                    />
                  </div>

                  {section.fields.map(field => {
                    const fieldClasses = classnames({
                      "doc-entry__field-input": true,
                      "doc-entry__field-input--checkbox": field.field_type === "boolean",
                    });

                    return (
                      <div
                        key={`${section.section_key}-${section.section_index}-${field.field_key}`}
                      >
                        <div className="doc-entry__field">
                          <div className={fieldClasses}>
                            {this.renderField(section, field, false)}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {section.allows_multiple && (
                    <div>
                      <Button
                        style={{ marginRight: "5px" }}
                        size="sm"
                        renderIcon={Add16}
                        kind="ghost"
                        onClick={() => addAnotherSection(section.section_key, indexOfSection)}
                      >
                        Add Another
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderField(section: DocSectionShape, field: DocFieldShape, isCalculated: boolean) {
    const { values } = this.props;

    const { section_key, section_index } = section;
    const { field_key, field_type, reference_text, field_value_options } = field;

    const sharedProps = {
      inputID: `doc-entry-${section_key}-${field_key}`,
      labelValue: DocEntryView.generateFieldLabel(field),
    };

    const val =
      values?.[section_key]?.[section_index]?.[field_key] !== undefined
        ? values[section_key][section_index][field_key]
        : "";

    switch (field_type) {
      case "boolean": {
        return (
          <LabeledInput
            {...sharedProps}
            inputType="boolean"
            inputName={field_key}
            inputValue={val}
            hint={reference_text}
            onChange={e => {
              this.onChangeBoolean(section_key, section_index, e);
            }}
            disabled={isCalculated}
          />
        );
      }
      case "date": {
        return (
          <LabeledInput
            {...sharedProps}
            hint={reference_text}
            inputType="date"
            inputName={field_key}
            inputValue={val}
            placeholder="yyyy-mm-dd"
            onChange={e => {
              this.onChangeText(section_key, section_index, e);
            }}
            disabled={isCalculated}
          />
        );
      }
      case "money": {
        return (
          <LabeledInput
            {...sharedProps}
            hint={reference_text}
            inputType="money"
            inputName={field_key}
            inputValue={val}
            onChange={e => {
              this.onChangeMoney(section_key, section_index, e);
            }}
            allowNegatives
            disabled={isCalculated}
          />
        );
      }
      case "number": {
        return (
          <LabeledInput
            {...sharedProps}
            hint={reference_text}
            inputType="text"
            inputName={field_key}
            inputValue={val}
            onChange={e => {
              this.onChangeNumber(section_key, section_index, e);
            }}
            disabled={isCalculated}
          />
        );
      }
      case "address": {
        return (
          <AddressFieldsWithValidation
            {...sharedProps}
            address_line1={val ? val.address_line1 : ""}
            hint={reference_text}
            address_line2={val ? val.address_line2 : ""}
            city={val ? val.city : ""}
            state={val ? val.state : ""}
            postal_code={val ? val.postal_code : ""}
            callback={e => {
              this.onChangeAddress(section_key, section_index, field_key, e);
            }}
            skipValidation
            disabled={isCalculated}
          />
        );
      }
      case "select": {
        return (
          <LabeledInput
            {...sharedProps}
            hint={reference_text}
            inputType="select"
            inputName={field_key}
            inputValue={val}
            options={field_value_options}
            onChange={e => {
              this.onChangeSelect(section_key, section_index, field_key, e);
            }}
            disabled={isCalculated}
          />
        );
      }
      case "": // fall thru, default for untyped
      case "text": {
        return (
          <LabeledInput
            {...sharedProps}
            hint={reference_text}
            inputType="text"
            inputName={field_key}
            inputValue={val}
            onChange={e => {
              this.onChangeText(section_key, section_index, e);
            }}
            disabled={isCalculated}
          />
        );
      }
      default: {
        throw new Error(`unknown doc entry field type: ${field_type}`);
      }
    }
  }

  setStateFromClipBoard() {
    const { upsertValue } = this.props;
    navigator.clipboard
      .readText()
      .then(text => {
        const parsed = JSON.parse(text);
        const sectionKeys = Object.keys(parsed);
        for (let i = 0; i < sectionKeys.length; i = i + 1) {
          const sectionKey = sectionKeys[i];
          const section = parsed[sectionKey];
          const sectionIndexKeys = Object.keys(section);
          for (let j = 0; j < sectionIndexKeys.length; j += 1) {
            const sectionIndexKey = sectionIndexKeys[j];
            const sectionIndex = section[sectionIndexKey];
            const fieldKeys = Object.keys(sectionIndex);
            for (let k = 0; k < fieldKeys.length; k += 1) {
              const fieldKey = fieldKeys[k];
              const value = section[fieldKey];
              upsertValue(sectionKey, sectionIndex, fieldKey, value);
            }
          }
        }
      })
      .catch(err => {
        alert("The text in your clipboard wasn't able to be pasted!");
      });
  }

  renderMenuButton = () => {
    const { values } = this.props;
    return (
      <span className="doc-entry__modal-change-doc-info">
        <Button
          style={{ marginRight: "5px" }}
          size="sm"
          renderIcon={Copy16}
          kind="ghost"
          onClick={() => updateClipboard(JSON.stringify(values))}
        >
          Copy fields
        </Button>
        <Button
          style={{ marginRight: "5px" }}
          size="sm"
          renderIcon={Paste16}
          kind="ghost"
          onClick={() => this.setStateFromClipBoard()}
        >
          Paste fields
        </Button>
      </span>
    );
  };

  render() {
    const { docNotFoundMsg, modalTitle, hideButton, onHide, docs } = this.props;

    if (!docs || !docs.length) {
      return <div className="doc-entry__container">{docNotFoundMsg}</div>;
    }

    return (
      <div className="doc-entry">
        <h2>{modalTitle}</h2>
        <div className="doc-entry__header">{this.renderMenuButton()}</div>
        <div>
          <div className="doc-entry__container">
            <div className="doc-entry__pdf">
              <DocDisplay docs={docs} />
            </div>
            {docs && docs[0].sections && this.renderSidebar()}
          </div>
        </div>
        {docs && docs.length && hideButton !== true && (
          <footer className={styles.footer}>
            <Button kind="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button kind="primary" onClick={this.saveValues} renderIcon={Save16}>
              Save changes
            </Button>
          </footer>
        )}
      </div>
    );
  }
}

export default DocEntryView;
